import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Alert, Switch, FormControlLabel,
  Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton, Tooltip,
  CircularProgress, FormControl, InputLabel, Select, Checkbox, FormGroup, Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon, Security as SecurityIcon, PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon, Block as BlockIcon, ContentCopy as CopyIcon,
  Palette as PaletteIcon, Language as LanguageIcon, Notifications as NotificationsIcon,
  InfoOutlined as InfoOutlinedIcon, AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import {
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Add as AddIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { preferencesApi, authApi, apiKeysApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import { useThemePreferences } from '../theme/ThemeContext';
import { isDyslexiaTheme } from '../theme/theme';
import type { PlanningPreference } from '../types/models';

// MFA Setup Section
function MfaSetupSection() {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [setupData, setSetupData] = useState<{ sharedKey: string; authenticatorUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then(data => setMfaEnabled(data.mfaEnabled)).catch(() => {});
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.setupMfa();
      setSetupData(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.enableMfa(code);
      setRecoveryCodes(data.recoveryCodes);
      setMfaEnabled(true);
      setSuccess('MFA enabled successfully');
      setSetupData(null);
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (mfaEnabled === null) return null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <SecurityIcon />
          <Typography variant="h6">Multi-Factor Authentication</Typography>
          {mfaEnabled && <Chip label="Enabled" color="success" size="small" icon={<CheckCircleIcon />} />}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {mfaEnabled && !recoveryCodes && (
          <Typography color="text.secondary">
            MFA is enabled on your account. Your login requires a verification code from your authenticator app.
          </Typography>
        )}

        {!mfaEnabled && !setupData && (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Add an extra layer of security to your account by enabling MFA with an authenticator app (Microsoft Authenticator, Google Authenticator, etc.).
            </Typography>
            <Button variant="outlined" onClick={handleSetup} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SecurityIcon />}>
              Set Up MFA
            </Button>
          </Box>
        )}

        {setupData && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Scan this key in your authenticator app, or enter it manually:
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TextField
                value={setupData.sharedKey}
                fullWidth
                size="small"
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', letterSpacing: 2 } }}
              />
              <Tooltip title="Copy key">
                <IconButton onClick={() => copyKey(setupData.sharedKey)} size="small"><CopyIcon /></IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              App name: <strong>Cadence</strong> &middot; Account: <strong>{user?.email}</strong>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ mb: 1 }}>Enter the 6-digit code from your authenticator app to verify:</Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                size="small"
                inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', fontSize: 18, letterSpacing: 8, textAlign: 'center' } }}
                sx={{ width: 180 }}
              />
              <Button variant="contained" onClick={handleEnable} disabled={loading || code.length !== 6}>
                {loading ? 'Verifying...' : 'Enable MFA'}
              </Button>
            </Stack>
          </Box>
        )}

        {recoveryCodes && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Save these recovery codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
            </Alert>
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
              {recoveryCodes.map((rc, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>{rc}</Typography>
              ))}
            </Box>
            <Button size="small" sx={{ mt: 1 }} onClick={() => copyKey(recoveryCodes.join('\n'))}>
              Copy All Codes
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// Account Info Section (for non-admin users: Executive, EA)
function AccountInfoSection() {
  const { user } = useAuth();
  if (!user) return null;

  const roleLabel = (role: string) => {
    switch (role) {
      case 'ExecutiveAssistant': return 'Executive Assistant';
      case 'TenantAdmin': return 'Tenant Admin';
      case 'PlatformAdmin': return 'Platform Admin';
      default: return role;
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <PersonIcon />
          <Typography variant="h6">Account</Typography>
        </Stack>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600} color="text.primary">{user.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
            </Box>
            <Chip label={roleLabel(user.role)} size="small" color="primary" variant="outlined" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// API Keys Section
function ApiKeysSection() {
  const [keys, setKeys] = useState<Array<{
    id: string; name: string; keyPrefix: string; createdAt: string;
    expiresAt: string | null; lastUsedAt: string | null; scopes: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState<string>('90');
  const [createLoading, setCreateLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const data = await apiKeysApi.list();
      setKeys(data);
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreateLoading(true);
    setError(null);
    try {
      const expiryDays = newKeyExpiry === 'never' ? undefined : parseInt(newKeyExpiry);
      const result = await apiKeysApi.create({ name: newKeyName.trim(), expiryDays });
      setCreatedKey(result.key);
      setNewKeyName('');
      setNewKeyExpiry('90');
      loadKeys();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await apiKeysApi.revoke(id);
      loadKeys();
    } catch {
      setError('Failed to revoke key');
    }
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VpnKeyIcon />
            <Typography variant="h6">Your API Keys</Typography>
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setShowCreate(true); setCreatedKey(null); }}
          >
            Generate New Key
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Newly created key banner */}
        {createdKey && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Save this key now -- it will not be shown again.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                value={createdKey}
                fullWidth
                size="small"
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <Tooltip title={copySuccess ? 'Copied!' : 'Copy key'}>
                <IconButton onClick={handleCopyKey} size="small">
                  {copySuccess ? <CheckCircleIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Alert>
        )}

        {/* Key list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : keys.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No API keys yet. Generate one to connect the MCP server or external integrations.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {keys.map(k => (
              <Box
                key={k.id}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 1.5, bgcolor: 'action.hover', borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>{k.name}</Typography>
                    <Chip label={k.keyPrefix + '...'} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Created {formatDate(k.createdAt)}
                    {k.lastUsedAt && <> &middot; Last used {formatDate(k.lastUsedAt)}</>}
                    {k.expiresAt && <> &middot; Expires {formatDate(k.expiresAt)}</>}
                    {!k.expiresAt && <> &middot; No expiry</>}
                  </Typography>
                </Box>
                <Tooltip title="Revoke key">
                  <IconButton size="small" color="error" onClick={() => handleRevoke(k.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}

        {/* Create dialog */}
        <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Generate New API Key</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Key Name"
                fullWidth
                placeholder="e.g. MCP Server, Claude Desktop"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                helperText="A friendly name to identify this key"
              />
              <TextField
                label="Expiry"
                select
                fullWidth
                value={newKeyExpiry}
                onChange={e => setNewKeyExpiry(e.target.value)}
              >
                <MenuItem value="30">30 days</MenuItem>
                <MenuItem value="90">90 days</MenuItem>
                <MenuItem value="180">180 days</MenuItem>
                <MenuItem value="365">1 year</MenuItem>
                <MenuItem value="never">Never expires</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createLoading || !newKeyName.trim()}
            >
              {createLoading ? 'Generating...' : 'Generate Key'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// User Management Section (only for PlatformAdmin/Admin roles)
function UserManagementSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState<Array<{
    id: string; email: string; displayName: string; role: string;
    isActive: boolean; createdAt: string; lastLoginAt: string | null; mfaEnabled: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Only PlatformAdmin and Admin should see this section
  const isAdmin = user?.role === 'PlatformAdmin' || user?.role === 'Admin';

  const loadUsers = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.status === 403 ? 'Insufficient permissions' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleActive = async (id: string) => {
    try {
      await authApi.toggleUserActive(id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pw = '';
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.displayName || !newUser.password) {
      setAddError('All fields are required');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await authApi.registerUser({
        email: newUser.email,
        password: newUser.password,
        displayName: newUser.displayName,
        tenantId: user?.tenantId || '',
        role: newUser.role,
      });
      setShowAdd(false);
      setNewUser({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
      loadUsers();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setAddError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  // Don't render for non-admin roles
  if (!isAdmin) return null;
  if (loading) return null;
  if (error && users.length === 0) return null; // Hide section if no permission

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon />
            <Typography variant="h6">User Management</Typography>
          </Stack>
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => setShowAdd(true)}>
            Register User
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack spacing={1}>
          {users.map(u => (
            <Box
              key={u.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600} color="text.primary">
                  {u.displayName}
                  {u.id === user?.id && <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">{u.email}</Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip label={u.role} size="small" color="primary" variant="outlined" />
                {u.isActive
                  ? <Chip label="Active" size="small" color="success" variant="outlined" />
                  : <Chip label="Inactive" size="small" color="error" />
                }
                {u.mfaEnabled && <Chip label="MFA" size="small" color="success" />}
                {u.id !== user?.id && (
                  <Tooltip title={u.isActive ? 'Deactivate user' : 'Reactivate user'}>
                    <IconButton size="small" onClick={() => handleToggleActive(u.id)}>
                      {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Add User Dialog */}
        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Register New User</DialogTitle>
          <DialogContent>
            {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Display Name" fullWidth value={newUser.displayName} onChange={e => setNewUser(prev => ({ ...prev, displayName: e.target.value }))} />
              <TextField label="Email" type="email" fullWidth value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
              <Stack direction="row" spacing={1}>
                <TextField label="Password" fullWidth value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} helperText="Min 8 chars, upper, lower, digit, special" />
                <Button variant="text" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => setNewUser(prev => ({ ...prev, password: generatePassword() }))}>Generate</Button>
              </Stack>
              <TextField label="Role" select fullWidth value={newUser.role} onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}>
                <MenuItem value="Executive">Executive</MenuItem>
                <MenuItem value="ExecutiveAssistant">Executive Assistant</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddUser} disabled={addLoading}>
              {addLoading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const COMMON_TIMEZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'UTC',
];

const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark', description: 'Dark background, light text' },
  { value: 'light', label: 'Light', description: 'Light background, dark text' },
  { value: 'system', label: 'System', description: 'Match your operating system setting' },
] as const;

const FONT_OPTIONS = [
  { value: 'default', label: 'Default (Inter)', description: 'Clean, modern typeface' },
  { value: 'dyslexia', label: 'Dyslexia friendly', description: 'Optimised spacing, font, and contrast for easier reading' },
  { value: 'dyslexia-hc', label: 'Dyslexia friendly (high contrast)', description: 'Same as above with stronger contrast' },
] as const;

const FONT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small', description: '12px' },
  { value: 'medium', label: 'Medium', description: '14px' },
  { value: 'large', label: 'Large', description: '16px' },
  { value: 'xl', label: 'Extra Large', description: '18px' },
] as const;

// Main Preferences Page
export default function PreferencesPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Partial<PlanningPreference>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Theme preferences via context (context handles localStorage)
  const { preferences: themePrefs, setPreferences: setThemePrefs } = useThemePreferences();

  // Display preferences (stored locally for now)
  const [timezone, setTimezone] = useState(() => localStorage.getItem('cadence_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [notifDailyBriefing, setNotifDailyBriefing] = useState(() => localStorage.getItem('cadence_notif_daily') !== 'false');
  const [notifPlanProposed, setNotifPlanProposed] = useState(() => localStorage.getItem('cadence_notif_plan') !== 'false');
  const [notifCarryForward, setNotifCarryForward] = useState(() => localStorage.getItem('cadence_notif_carry') !== 'false');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [configInput, setConfigInput] = useState('');
  const [configOutput, setConfigOutput] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [showConfigHelper, setShowConfigHelper] = useState(false);
  const [aiApiKey, setAiApiKey] = useState(() => localStorage.getItem('cadence_ai_api_key') || '');
  const [aiProvider, setAiProvider] = useState<'system' | 'openai' | 'google' | 'anthropic'>('system');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await preferencesApi.get();
        setPrefs(data);
        // Derive AI provider from saved model
        const model = data.preferredAiModel || '';
        if (model.startsWith('gpt-') || model.startsWith('o1') || model.startsWith('o3') || model.startsWith('o4')) {
          setAiProvider('openai');
        } else if (model.startsWith('gemini-')) {
          setAiProvider('google');
        } else if (model.startsWith('claude-') && model !== 'claude-sonnet-4-5-20250514') {
          setAiProvider('anthropic');
        } else {
          setAiProvider('system');
        }
      } catch (err) {
        console.error('Failed to load preferences', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    // Persist display preferences locally (theme is handled by ThemeContext)
    localStorage.setItem('cadence_timezone', timezone);
    localStorage.setItem('cadence_notif_daily', String(notifDailyBriefing));
    localStorage.setItem('cadence_notif_plan', String(notifPlanProposed));
    localStorage.setItem('cadence_notif_carry', String(notifCarryForward));
    if (aiApiKey) localStorage.setItem('cadence_ai_api_key', aiApiKey);
    try {
      // Only send fields matching UpdatePreferencesRequest, with TimeOnly format
      const toTimeOnly = (v?: string) => v ? (v.length === 5 ? `${v}:00` : v) : undefined;
      const payload = {
        workDayStart: toTimeOnly(prefs.workDayStart),
        workDayEnd: toTimeOnly(prefs.workDayEnd),
        meetingBufferMinutes: prefs.meetingBufferMinutes,
        meetingPrepMinutes: prefs.meetingPrepMinutes,
        maxFocusBlockMinutes: prefs.maxFocusBlockMinutes,
        minBlockMinutes: prefs.minBlockMinutes,
        lunchStartMinute: prefs.lunchStartMinute,
        lunchDurationMinutes: prefs.lunchDurationMinutes,
        icsCalendarUrl: prefs.icsCalendarUrl || undefined,
        treatPrivateAsPriority: prefs.treatPrivateAsPriority,
        preferredAiModel: prefs.preferredAiModel || undefined,
      };
      await preferencesApi.update(payload);
      setMessage({ type: 'success', text: 'Preferences saved' });
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (typeof data === 'string') msg = data;
      else if (data?.title) msg = data.title;
      else if (data?.errors) {
        const errs = Object.values(data.errors).flat();
        msg = errs.join(', ');
      } else if (data?.error) msg = data.error;
      else if (err.message) msg = err.message;
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Settings</Typography>
          <Typography variant="subtitle1">Manage your account, security, and preferences</Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Stack spacing={2.5}>
        {/* Account Info (shown for all users) */}
        <AccountInfoSection />

        {/* MFA Setup */}
        <MfaSetupSection />

        {/* User Management (only for PlatformAdmin/Admin) */}
        <UserManagementSection />

        <Divider />

        {/* Working Hours */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Working Hours</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Day Start"
                type="time"
                fullWidth
                value={prefs.workDayStart || '08:00'}
                onChange={e => setPrefs(prev => ({ ...prev, workDayStart: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Day End"
                type="time"
                fullWidth
                value={prefs.workDayEnd || '18:00'}
                onChange={e => setPrefs(prev => ({ ...prev, workDayEnd: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Lunch Start"
                type="time"
                fullWidth
                value={(() => {
                  const mins = prefs.lunchStartMinute ?? 720;
                  const h = Math.floor(mins / 60).toString().padStart(2, '0');
                  const m = (mins % 60).toString().padStart(2, '0');
                  return `${h}:${m}`;
                })()}
                onChange={e => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  if (!isNaN(h) && !isNaN(m)) {
                    setPrefs(prev => ({ ...prev, lunchStartMinute: h * 60 + m }));
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Lunch Duration (min)"
                type="number"
                fullWidth
                value={prefs.lunchDurationMinutes || 60}
                onChange={e => setPrefs(prev => ({ ...prev, lunchDurationMinutes: parseInt(e.target.value) }))}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scheduling Rules</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Meeting Buffer (min)" type="number" fullWidth value={prefs.meetingBufferMinutes ?? 10} onChange={e => setPrefs(prev => ({ ...prev, meetingBufferMinutes: parseInt(e.target.value) }))} helperText="Buffer after each meeting" />
              <TextField label="Meeting Prep (min)" type="number" fullWidth value={prefs.meetingPrepMinutes ?? 5} onChange={e => setPrefs(prev => ({ ...prev, meetingPrepMinutes: parseInt(e.target.value) }))} helperText="Prep time before meetings" />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField label="Max Focus Block (min)" type="number" fullWidth value={prefs.maxFocusBlockMinutes ?? 90} onChange={e => setPrefs(prev => ({ ...prev, maxFocusBlockMinutes: parseInt(e.target.value) }))} helperText="Longest uninterrupted work block" />
              <TextField label="Min Block (min)" type="number" fullWidth value={prefs.minBlockMinutes ?? 15} onChange={e => setPrefs(prev => ({ ...prev, minBlockMinutes: parseInt(e.target.value) }))} helperText="Shortest schedulable block" />
            </Stack>
          </CardContent>
        </Card>

        {/* Calendar — redirect to Calendar page */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Calendar Integration</Typography>
            <Typography variant="body2" color="text.secondary">
              Connect and manage your calendars from the <strong>Calendar</strong> page.
              Click the settings icon on the Calendar page to connect Microsoft 365, Google Calendar, or ICS feeds.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={prefs.treatPrivateAsPriority ?? true}
                  onChange={e => setPrefs(prev => ({ ...prev, treatPrivateAsPriority: e.target.checked }))}
                />
              }
              label="Treat private calendar events as priority meetings"
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>

        {/* AI */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>AI Configuration</Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={aiProvider}
                label="Provider"
                onChange={e => {
                  const provider = e.target.value as typeof aiProvider;
                  setAiProvider(provider);
                  if (provider === 'system') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: undefined }));
                  } else if (provider === 'openai') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'gpt-4o' }));
                  } else if (provider === 'google') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'gemini-2.0-flash' }));
                  } else if (provider === 'anthropic') {
                    setPrefs(prev => ({ ...prev, preferredAiModel: 'claude-sonnet-4-5-20250514' }));
                  }
                }}
              >
                <MenuItem value="system">System Default</MenuItem>
                <MenuItem value="openai">ChatGPT (OpenAI)</MenuItem>
                <MenuItem value="google">Gemini (Google)</MenuItem>
                <MenuItem value="anthropic">Claude (Anthropic)</MenuItem>
              </Select>
            </FormControl>

            {/* System Default info */}
            {aiProvider === 'system' && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Uses Cadence's built-in AI (currently Anthropic Claude). No configuration needed.
              </Alert>
            )}

            {/* OpenAI */}
            {aiProvider === 'openai' && (
              <Stack spacing={2}>
                <TextField
                  label="OpenAI API Key"
                  fullWidth
                  type="password"
                  value={aiApiKey}
                  onChange={e => setAiApiKey(e.target.value)}
                  placeholder="sk-..."
                />
                <FormControl fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={prefs.preferredAiModel || 'gpt-4o'}
                    label="Model"
                    onChange={e => setPrefs(prev => ({ ...prev, preferredAiModel: e.target.value }))}
                  >
                    <MenuItem value="gpt-4o">GPT-4o (recommended)</MenuItem>
                    <MenuItem value="gpt-4o-mini">GPT-4o Mini (faster, lower cost)</MenuItem>
                    <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* Google Gemini */}
            {aiProvider === 'google' && (
              <Stack spacing={2}>
                <TextField
                  label="Google AI API Key"
                  fullWidth
                  type="password"
                  value={aiApiKey}
                  onChange={e => setAiApiKey(e.target.value)}
                  placeholder="AIza..."
                />
                <FormControl fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={prefs.preferredAiModel || 'gemini-2.0-flash'}
                    label="Model"
                    onChange={e => setPrefs(prev => ({ ...prev, preferredAiModel: e.target.value }))}
                  >
                    <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash (recommended)</MenuItem>
                    <MenuItem value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* Anthropic Claude (own key) */}
            {aiProvider === 'anthropic' && (
              <Stack spacing={2}>
                <Alert severity="info" sx={{ mb: 0 }}>
                  Use your own Anthropic API key instead of the system one.
                </Alert>
                <TextField
                  label="Anthropic API Key"
                  fullWidth
                  type="password"
                  value={aiApiKey}
                  onChange={e => setAiApiKey(e.target.value)}
                  placeholder="sk-ant-..."
                />
                <FormControl fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={prefs.preferredAiModel || 'claude-sonnet-4-5-20250514'}
                    label="Model"
                    onChange={e => setPrefs(prev => ({ ...prev, preferredAiModel: e.target.value }))}
                  >
                    <MenuItem value="claude-sonnet-4-5-20250514">Claude Sonnet 4.5 (recommended)</MenuItem>
                    <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (faster, lower cost)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}

            {aiProvider !== 'system' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Your API key is stored securely and only used for your account.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Divider />

        {/* Timezone */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LanguageIcon />
              <Typography variant="h6">Timezone</Typography>
            </Stack>
            <Autocomplete
              options={COMMON_TIMEZONES}
              value={timezone}
              onChange={(_, val) => setTimezone(val || 'Europe/London')}
              getOptionLabel={(opt) => opt.replace(/_/g, ' ')}
              renderInput={(params) => <TextField {...params} label="Timezone" />}
              disableClearable
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Display-only preference. All scheduling uses your system timezone. Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </Typography>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PaletteIcon />
              <Typography variant="h6">Theme</Typography>
            </Stack>
            {isDyslexiaTheme(themePrefs.fontFamily) && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Theme mode is managed by your reading preference below.
              </Typography>
            )}
            <Stack direction="row" spacing={1.5} sx={{ opacity: isDyslexiaTheme(themePrefs.fontFamily) ? 0.4 : 1, pointerEvents: isDyslexiaTheme(themePrefs.fontFamily) ? 'none' : 'auto' }}>
              {THEME_OPTIONS.map(opt => (
                <Card
                  key={opt.value}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: themePrefs.mode === opt.value ? 'primary.main' : 'divider',
                    transition: 'border-color 0.2s',
                  }}
                  onClick={() => setThemePrefs({ ...themePrefs, mode: opt.value })}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Accessibility */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <InfoOutlinedIcon />
              <Typography variant="h6">Accessibility</Typography>
            </Stack>

            {/* High Contrast */}
            <FormControlLabel
              disabled={isDyslexiaTheme(themePrefs.fontFamily)}
              control={
                <Switch
                  checked={themePrefs.highContrast}
                  onChange={e => setThemePrefs({ ...themePrefs, highContrast: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">High Contrast Mode</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isDyslexiaTheme(themePrefs.fontFamily)
                      ? 'Use "Dyslexia friendly (high contrast)" above instead'
                      : 'Increases contrast for better readability'}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, ml: 0 }}
            />

            <Divider sx={{ mb: 2 }} />

            {/* Font & Reading Preferences */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Reading Preference</Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {FONT_OPTIONS.map(opt => (
                <Card
                  key={opt.value}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    border: 2,
                    borderColor: themePrefs.fontFamily === opt.value ? 'primary.main' : 'divider',
                    transition: 'border-color 0.2s',
                  }}
                  onClick={() => setThemePrefs({ ...themePrefs, fontFamily: opt.value as any })}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Font Size */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Font Size</Typography>
            {isDyslexiaTheme(themePrefs.fontFamily) && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Font size is optimised by your reading preference.
              </Typography>
            )}
            <Stack direction="row" spacing={1.5} sx={{ mb: 2, opacity: isDyslexiaTheme(themePrefs.fontFamily) ? 0.4 : 1, pointerEvents: isDyslexiaTheme(themePrefs.fontFamily) ? 'none' : 'auto' }}>
              {FONT_SIZE_OPTIONS.map(opt => (
                <Card
                  key={opt.value}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: themePrefs.fontSize === opt.value ? 'primary.main' : 'divider',
                    transition: 'border-color 0.2s',
                  }}
                  onClick={() => setThemePrefs({ ...themePrefs, fontSize: opt.value })}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Reduced Motion */}
            <FormControlLabel
              control={
                <Switch
                  checked={themePrefs.reducedMotion}
                  onChange={e => setThemePrefs({ ...themePrefs, reducedMotion: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Reduce Motion</Typography>
                  <Typography variant="caption" color="text.secondary">Minimise animations and transitions</Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <NotificationsIcon />
              <Typography variant="h6">Notification Preferences</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which email notifications you would like to receive.
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={notifDailyBriefing} onChange={e => setNotifDailyBriefing(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2">Email daily briefing</Typography>
                    <Typography variant="caption" color="text.secondary">Receive a summary of your planned day each morning</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={notifPlanProposed} onChange={e => setNotifPlanProposed(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2">Email when plan proposed by EA</Typography>
                    <Typography variant="caption" color="text.secondary">Get notified when your EA submits a daily plan for review</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={notifCarryForward} onChange={e => setNotifCarryForward(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2">Email carry-forward reminders</Typography>
                    <Typography variant="caption" color="text.secondary">Reminder when tasks are carried forward to the next day</Typography>
                  </Box>
                }
              />
            </FormGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Notification preferences are saved and will take effect when email notifications are configured for your organisation.
            </Typography>
          </CardContent>
        </Card>

        <Divider />

        {/* Integrations */}
        <Typography variant="h5" sx={{ mt: 1 }}>Integrations</Typography>

        {/* API Keys */}
        <ApiKeysSection />

        {/* MCP / AI Assistant Integration */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AutoAwesomeIcon />
              <Typography variant="h6">AI Assistant Integration</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect Cadence to Claude Code or other MCP-compatible AI assistants.
              The MCP server gives your AI assistant access to 40+ Cadence tools including daily planning, work items, calendar, and transcripts.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate an API key above, then follow the instructions for your Claude app below.
              Replace <code>cad_k1_paste_your_key_here</code> with the key you generated.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Claude Desktop */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Claude Desktop</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              1. Open Claude Desktop and go to <strong>Settings</strong><br/>
              2. Scroll to <strong>Integrations</strong> and click <strong>Add custom connector</strong><br/>
              3. Enter the details below and click <strong>Add</strong>:
            </Typography>
            <Box sx={{ pl: 2, mb: 1 }}>
              <Typography variant="body2"><strong>Name:</strong> Cadence</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>Remote MCP server URL:</strong> https://api.stratiumhq.com/mcp?key=<em>your_api_key</em>
              </Typography>
              <Typography variant="body2" color="text.secondary">Leave OAuth fields blank.</Typography>
            </Box>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              sx={{ mt: 0.5 }}
              onClick={() => {
                navigator.clipboard.writeText('https://api.stratiumhq.com/mcp?key=cad_k1_paste_your_key_here');
                setCopySuccess('mcp-desktop');
                setTimeout(() => setCopySuccess(null), 2000);
              }}
            >
              {copySuccess === 'mcp-desktop' ? 'Copied!' : 'Copy Server URL'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              4. Replace <code>cad_k1_paste_your_key_here</code> with the API key you generated above.
              Cadence will appear as a connector with 40+ tools.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Claude Code */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Claude Code (Terminal)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Run this single command in your terminal:
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'action.hover',
                color: 'text.primary',
                p: 2,
                borderRadius: 1,
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
{`claude mcp add cadence \\
  --transport sse \\
  "https://api.stratiumhq.com/mcp?key=cad_k1_paste_your_key_here"`}
            </Box>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              sx={{ mt: 1 }}
              onClick={() => {
                navigator.clipboard.writeText(
                  `claude mcp add cadence --transport sse "https://api.stratiumhq.com/mcp?key=cad_k1_paste_your_key_here"`
                );
                setCopySuccess('mcp-code');
                setTimeout(() => setCopySuccess(null), 2000);
              }}
            >
              {copySuccess === 'mcp-code' ? 'Copied!' : 'Copy Command'}
            </Button>

            <Alert severity="info" sx={{ mt: 3 }} icon={<InfoOutlinedIcon />}>
              No additional software required — just Claude Desktop or Claude Code.
              API keys are stored as SHA-256 hashes — the plaintext is never stored on the server.
            </Alert>
          </CardContent>
        </Card>

        {/* API Access */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LanguageIcon />
              <Typography variant="h6">API Access</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Integrate Cadence directly into your own tools and workflows via the REST API.
              Generate an API key above to authenticate.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TextField
                value="https://api.stratiumhq.com/api"
                fullWidth
                size="small"
                label="API Base URL"
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
              />
              <Tooltip title="Copy URL">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText('https://api.stratiumhq.com/api');
                    setCopySuccess('api');
                    setTimeout(() => setCopySuccess(null), 2000);
                  }}
                >
                  {copySuccess === 'api' ? <CheckCircleIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Use your API key in the Authorization header: <code>Authorization: Bearer cad_k1_...</code>
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
